import { GAME_CONSTANTS } from '@tankworld/shared';
import { distanceVec3, dotVec3, lengthVec3, subVec3 } from './math.js';
export class CollisionSystem {
    terrain;
    constructor(terrain) {
        this.terrain = terrain;
    }
    /**
     * Clamps tank position to stay inside arena boundaries.
     */
    clampToArena(pos, radius) {
        let clamped = false;
        const minBound = GAME_CONSTANTS.ARENA_BOUNDARY_MIN + radius;
        const maxBound = GAME_CONSTANTS.ARENA_BOUNDARY_MAX - radius;
        if (pos.x < minBound) {
            pos.x = minBound;
            clamped = true;
        }
        if (pos.x > maxBound) {
            pos.x = maxBound;
            clamped = true;
        }
        if (pos.z < minBound) {
            pos.z = minBound;
            clamped = true;
        }
        if (pos.z > maxBound) {
            pos.z = maxBound;
            clamped = true;
        }
        return clamped;
    }
    /**
     * Resolves collision between tank position and static obstacles.
     */
    resolveObstacleCollisions(pos, radius, obstacles) {
        let collided = false;
        for (const obs of obstacles) {
            const dx = pos.x - obs.position.x;
            const dz = pos.z - obs.position.z;
            const distSq = dx * dx + dz * dz;
            const minDistance = radius + obs.radius;
            if (distSq < minDistance * minDistance && distSq > 0.0001) {
                const dist = Math.sqrt(distSq);
                const overlap = minDistance - dist;
                const nx = dx / dist;
                const nz = dz / dist;
                // Push tank out along collision normal
                pos.x += nx * overlap;
                pos.z += nz * overlap;
                collided = true;
            }
        }
        return collided;
    }
    /**
     * Resolves mutual collision between two tanks.
     */
    resolveTankVsTank(posA, posB, radius = 2.4) {
        const dx = posB.x - posA.x;
        const dz = posB.z - posA.z;
        const distSq = dx * dx + dz * dz;
        const minDistance = radius * 2;
        if (distSq < minDistance * minDistance && distSq > 0.0001) {
            const dist = Math.sqrt(distSq);
            const overlap = (minDistance - dist) * 0.5;
            const nx = dx / dist;
            const nz = dz / dist;
            posA.x -= nx * overlap;
            posA.z -= nz * overlap;
            posB.x += nx * overlap;
            posB.z += nz * overlap;
            return true;
        }
        return false;
    }
    /**
     * Raycast along segment from start to end against terrain, obstacles, and optional targets.
     */
    raycastSegment(start, end, obstacles, targets = [], ignoreId) {
        const dir = subVec3(end, start);
        const segLength = lengthVec3(dir);
        if (segLength < 1e-5) {
            return { hit: false, distance: 0, point: start };
        }
        const unitDir = { x: dir.x / segLength, y: dir.y / segLength, z: dir.z / segLength };
        let closestHit = { hit: false, distance: segLength, point: end };
        // 1. Raycast against targets (tanks)
        for (const target of targets) {
            if (target.id === ignoreId)
                continue;
            const toTarget = subVec3(target.position, start);
            const projection = dotVec3(toTarget, unitDir);
            if (projection > 0 && projection < closestHit.distance) {
                // Nearest point on ray to target center
                const closestPointOnRay = {
                    x: start.x + unitDir.x * projection,
                    y: start.y + unitDir.y * projection,
                    z: start.z + unitDir.z * projection,
                };
                const distToCenter = distanceVec3(closestPointOnRay, target.position);
                if (distToCenter <= target.radius) {
                    closestHit = {
                        hit: true,
                        distance: projection,
                        point: closestPointOnRay,
                        targetId: target.id,
                    };
                }
            }
        }
        // 2. Raycast against static obstacles
        for (const obs of obstacles) {
            const toObs = subVec3(obs.position, start);
            const projection = dotVec3(toObs, unitDir);
            if (projection > 0 && projection < closestHit.distance) {
                const closestPointOnRay = {
                    x: start.x + unitDir.x * projection,
                    y: start.y + unitDir.y * projection,
                    z: start.z + unitDir.z * projection,
                };
                const distToCenter = distanceVec3(closestPointOnRay, obs.position);
                if (distToCenter <= obs.radius) {
                    closestHit = {
                        hit: true,
                        distance: projection,
                        point: closestPointOnRay,
                        obstacleId: obs.id,
                    };
                }
            }
        }
        // 3. Terrain height intersection test along segment steps
        const steps = Math.ceil(segLength / 1.5);
        for (let i = 1; i <= steps; i++) {
            const t = i / steps;
            const testDist = t * segLength;
            if (testDist >= closestHit.distance)
                break;
            const px = start.x + unitDir.x * testDist;
            const py = start.y + unitDir.y * testDist;
            const pz = start.z + unitDir.z * testDist;
            const groundY = this.terrain.getHeight(px, pz);
            if (py <= groundY) {
                closestHit = {
                    hit: true,
                    distance: testDist,
                    point: { x: px, y: groundY, z: pz },
                    normal: this.terrain.getNormal(px, pz),
                };
                break;
            }
        }
        return closestHit;
    }
}
//# sourceMappingURL=collision.js.map